/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Sunday, 17th February 2019
 *
 *  Portal - Login View/Registration Wizard/Company Profile
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import TextFieldWithValidation from 'components/InputFields/TextFieldWithValidation';
import AddressFormReadOnly from 'components/Forms/AddressFormReadOnly';

// Material UI Components
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import PasswordInput from 'components/InputFields/Password';
import { useStores } from 'stores/RootStore';

const ReviewProfiles = props => {
  const { classes } = props;
  const stores = useStores();

  const {
    type,
    userFirstName,
    userLastName,
    userPhone,
    userEmail,
    userAddress1,
    userCity,
    userState,
    userZip,
    companyName,
    companyPhone,
    companyAddress1,
    companyCity,
    companyState,
    companyZip,
    accountPassword,
    showPasswordText,
    togglePasswordText,
    handlePasswordChange,
    acknowledgedPolicies,
    isLoading,
    isRegistering,
    handleRegistration,
  } = stores.registrationStore;
  return (
    <form id='register-form' action=' ' onSubmit={handleRegistration} autoComplete='off'>
      <Grid container justify='center' className={classes.container}>
        <Grid item xs={12}>
          <Grid container direction='row' alignItems='baseline'>
            <Typography
              variant='body1'
              color='textSecondary'
              className={classes.sectionTitle}
              styles={{ display: 'inline-block' }}
            >
              Your profile...
            </Typography>
          </Grid>
          <Divider className={classes.sectionDivider} />
        </Grid>
        <Grid container direction='row' spacing={0}>
          <Grid item sm={6} xs={11}>
            <Grid container>
              <Grid item sm={5} xs={12}>
                <TextFieldWithValidation
                  id='rev-first-name'
                  type='text'
                  visible
                  readOnly
                  fullWidth
                  value={userFirstName}
                  label='First Name'
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      className: classes.input,
                    },
                  }}
                  InputLabelProps={{
                    className: classes.inputLabel,
                  }}
                />
              </Grid>
              <Grid item sm={7} xs={12}>
                <TextFieldWithValidation
                  id='rev-last-name'
                  type='text'
                  visible
                  readOnly
                  fullWidth
                  value={userLastName}
                  label='Last Name'
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      className: classes.input,
                    },
                  }}
                  InputLabelProps={{
                    className: classes.inputLabel,
                  }}
                />
              </Grid>
              <Grid item sm={5} xs={12}>
                <TextFieldWithValidation
                  id='rev-phone'
                  type='text'
                  visible
                  readOnly
                  fullWidth
                  value={userPhone}
                  label='Phone'
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      className: classes.input,
                    },
                  }}
                  InputLabelProps={{
                    className: classes.inputLabel,
                  }}
                />
              </Grid>
              <Grid item sm={7} xs={12}>
                <TextFieldWithValidation
                  id='rev-email'
                  type='text'
                  visible
                  readOnly
                  fullWidth
                  value={userEmail}
                  label='Email'
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      className: classes.input,
                      autoComplete: 'off',
                    },
                  }}
                  InputLabelProps={{
                    className: classes.inputLabel,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item sm={6} xs={12} className={classes.gridItem}>
            <AddressFormReadOnly
              visible={type === 'vendor'}
              address1={userAddress1}
              city={userCity}
              state={userState}
              zip={userZip}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} className={classes.gridItem}>
          <Typography variant='body1' color='textSecondary' className={classes.sectionTitle}>
            Company profile...
          </Typography>
          <Divider className={classes.sectionDivider} />
        </Grid>
        <Grid container direction='row' spacing={0}>
          <Grid item sm={6} xs={12}>
            <Grid container>
              <Grid item xs={12}>
                <TextFieldWithValidation
                  id='rev-company-name'
                  type='text'
                  visible
                  readOnly
                  fullWidth
                  value={companyName}
                  label='Company Name'
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      className: classes.input,
                    },
                  }}
                  InputLabelProps={{
                    className: classes.inputLabel,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextFieldWithValidation
                  id='rev-company-phone'
                  type='text'
                  visible
                  readOnly
                  fullWidth
                  value={companyPhone}
                  label='Company Phone'
                  InputProps={{
                    disableUnderline: true,
                    inputProps: {
                      className: classes.input,
                    },
                  }}
                  InputLabelProps={{
                    className: classes.inputLabel,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item sm={6} xs={12} className={classes.gridItem}>
            <AddressFormReadOnly
              visible={type !== 'invite'}
              address1={companyAddress1}
              city={companyCity}
              state={companyState}
              zip={companyZip}
            />
          </Grid>
        </Grid>
        <div className={classes.passwordContainer}>
          <div className={classes.registerPassword}>
            <PasswordInput
              id='password'
              autoComplete='off'
              disabled={isLoading || isRegistering}
              showPasswordText={showPasswordText}
              showPasswordToggle={togglePasswordText}
              InputProps={{
                value: accountPassword,
                onChange: handlePasswordChange,
                fullWidth: true,
                margin: 'dense',
                className: classes.formInput,
                inputProps: {
                  // eslint-disable-next-line
                  pattern: '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,}).*',
                  title: 'Must contain 8 or more characters, uppercase and lowercase letters, and at least one number.',
                },
              }}
            />
            <Grid container justify='center' style={{ marginLeft: '-35px', marginTop: '-15px' }}>
              <Typography variant='caption' style={{ textAlign: 'left' }}>
                <br />
                <strong>Your password must have:</strong>
                <br />
                {'- 8 or more characters.'}
                <br />
                {'- Uppercase & lowercase letters.'}
                <br />
                {'- At least one number.'}
              </Typography>
            </Grid>
          </div>
          <Grid container justify='center' className={classes.acknowledgeWrapper}>
            <div className={classes.acknowledgeBar}>
              <Typography variant='caption' color='textSecondary' className={classes.acknowledge}>
                {"By creating a new account, you agree to VendorBadge's "}
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href='http://www.vendorbadge.com/privacy-policy/'
                  className={classes.navLink}
                >
                  {'Privacy Policy '}
                </a>
                {'and '}
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href='http://www.vendorbadge.com/privacy-policy/'
                  className={classes.navLink}
                >
                  End User License Agreement
                </a>
              </Typography>
            </div>
          </Grid>
          <div className={classes.registerButton}>
            <Button
              variant='contained'
              disabled={accountPassword === '' || isLoading || isRegistering}
              type='submit'
              color='primary'
              className={classes.formButton}
              style={{ width: '275px' }}
            >
              Register
            </Button>
            {isRegistering && <CircularProgress size={24} className={classes.progress} />}
          </div>
        </div>
      </Grid>
    </form>
  );
};

ReviewProfiles.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styles = theme => ({
  sectionTitle: {
    marginTop: '10px',
    marginLeft: '-5px',
    fontWeight: 600,
    color: theme.palette.primary.light,
  },
  sectionDivider: {
    width: '100%',
    marginLeft: '-5px',
    marginTop: '3px',
    marginBottom: '5px',
  },
  input: {
    paddingTop: '1px',
    paddingRight: '3px',
    paddingleft: '3px',
    paddingBottom: '3px',
    fontSize: '10pt',
  },
  inputLabel: {
    fontSize: '11pt',
    [theme.breakpoints.down('xs')]: {
      fontSize: '10pt',
    },
    padding: '0 0 0 0 !important',
    margin: '0',
    marginTop: '4px',
    marginBottom: '-4px',
  },
  iconButton: {
    color: theme.palette.type === 'light' ? 'rgb(128, 128, 128)' : theme.palette.common.white,
    padding: '0',
  },
  margin: {
    margin: theme.spacing(1),
  },
  avatar: {
    width: '55px !important',
    height: '55px !important',
    marginTop: '5px',
    marginLeft: '-10px',
    marginRight: '10px',
    boxShadow: '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)',
  },
  formInput: {
    minWidth: '265px',
    marginBottom: '8px',
    fontSize: '12pt',
  },
  formButton: {
    marginTop: '15px',
    marginBottom: '0px',
    fontSize: '12pt',
  },
  navLink: {
    '&:link, &:visited': {
      textDecoration: 'none',
      color: theme.palette.text.primary,
    },
    '&:hover': {
      color: '#BD2025',
    },
    '&:active': {
      color: '#BD2025',
    },
  },
  acknowledgeWrapper: {
    padding: '10px',
    margin: '10px 0px 10px 0px',
  },
  acknowledgeBar: {
    maxWidth: '280px',
  },
  acknowledge: {
    textAlign: 'center',
  },
  registerPassword: {
    display: 'block',
    justifyContent: 'center',
    width: '100%',
  },
  registerButton: {
    display: 'flex',
    justifyContent: 'center',
  },
  passwordContainer: {
    [theme.breakpoints.up('sm')]: {
      marginBottom: '-35px',
    },
    textAlign: 'center',
    marginTop: '20px',
  },
  progress: {
    position: 'absolute',
    marginTop: '20px',
  },
});

export default withStyles(styles, { withTheme: true })(observer(ReviewProfiles));
